import { Listing, ListingList, OkResponse, type Product, formatBRL } from '@deizi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Field, Seg } from '../../components/Form';
import { Icon } from '../../components/Icon';
import { Modal } from '../../components/Overlays';
import { useToast } from '../../components/Toast';
import { api, newIdempotencyKey } from '../../lib/api';
import { useChannels } from '../../lib/catalog-api';
import { copyText, errorMessage } from '../../lib/messages';
import { productsKey, useInvalidateProducts } from '../../lib/products-api';
import { Combobox } from '../../components/Combobox';
import { channelOption } from '../../components/pickers';

const listingsKey = (id: string) => [...productsKey, id, 'listings'];

function priceLine(l: Listing): string {
  const priced = l.variants.filter((v) => v.listedPriceCentavos !== null);
  if (priced.length === 0) return 'sem preço';
  return priced.map((v) => `${v.name} ${formatBRL(v.listedPriceCentavos ?? 0)}`).join(' · ');
}

function ListingDialog({
  product,
  listing,
  onClose,
}: {
  product: Product;
  listing: Listing | null;
  onClose: () => void;
}) {
  const channels = useChannels();
  const qc = useQueryClient();
  const invalidate = useInvalidateProducts();
  const toast = useToast();
  const [key] = useState(newIdempotencyKey);
  const options = (channels.data?.data ?? []).filter(
    (c) => c.active || c.id === listing?.channel.id,
  );
  const [channelId, setChannelId] = useState(listing?.channel.id ?? options[0]?.id ?? '');
  const [title, setTitle] = useState(listing?.title ?? `${product.name} Impresso 3D`);
  const [externalId, setExternalId] = useState(listing?.externalId ?? '');
  const [url, setUrl] = useState(listing?.url ?? '');
  const [status, setStatus] = useState<'active' | 'paused'>(listing?.status ?? 'active');
  const [variantIds, setVariantIds] = useState<string[]>(
    listing ? listing.variants.map((v) => v.id) : product.variants.map((v) => v.id),
  );
  const done = () => {
    void qc.invalidateQueries({ queryKey: listingsKey(product.id) });
    invalidate();
  };
  const body = () => ({
    channelId: channelId || options[0]?.id,
    title: title.trim(),
    externalId: externalId.trim() || null,
    url: url.trim() || null,
    status,
    variantIds: variantIds.length === product.variants.length ? [] : variantIds,
  });
  const save = useMutation({
    mutationFn: () =>
      listing
        ? api.patch(`/listings/${listing.id}`, Listing, body())
        : api.post(`/products/${product.id}/listings`, Listing, body(), { idempotencyKey: key }),
    onSuccess: (l) => {
      done();
      toast(listing ? 'Anúncio salvo' : `Anúncio adicionado em ${l.channel.name}`);
      onClose();
    },
    onError: (err) => toast(errorMessage(err)),
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/listings/${listing?.id}`, OkResponse),
    onSuccess: () => {
      done();
      toast('Anúncio removido');
      onClose();
    },
  });
  const tooLong = title.length > 60;
  return (
    <Modal
      title={listing ? 'Editar anúncio' : 'Novo anúncio'}
      onClose={onClose}
      footer={
        <>
          {listing ? (
            <button
              type="button"
              className="btn g"
              style={{ color: 'var(--err)' }}
              onClick={() => remove.mutate()}
            >
              Remover
            </button>
          ) : null}
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn p"
            disabled={save.isPending || tooLong}
            onClick={() => {
              if (!title.trim()) {
                toast('Dê um título ao anúncio');
                return;
              }
              save.mutate();
            }}
          >
            Salvar anúncio
          </button>
        </>
      }
    >
      <Field id="ls-ch" label="Canal">
        <Combobox
          id="ls-ch"
          value={channelId}
          options={options.map(channelOption)}
          onChange={setChannelId}
        />
      </Field>
      <Field
        id="ls-title"
        label="Título"
        error={tooLong ? 'Até 60 caracteres' : null}
        help={`${title.length} de 60 caracteres`}
      >
        <input
          id="ls-title"
          className={tooLong ? 'input err' : 'input'}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 12 }}>
        <Field id="ls-id" label="ID do anúncio">
          <input
            id="ls-id"
            className="input tab"
            placeholder="MLB…"
            value={externalId}
            onChange={(e) => setExternalId(e.target.value)}
          />
        </Field>
        <Field id="ls-url" label="URL">
          <input
            id="ls-url"
            className="input"
            placeholder="https://…"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
        </Field>
      </div>
      <Field label="Situação">
        <Seg
          label="Situação do anúncio"
          value={status}
          options={[
            { value: 'active', label: 'Ativo' },
            { value: 'paused', label: 'Pausado' },
          ]}
          onChange={setStatus}
        />
      </Field>
      {product.variants.length > 1 ? (
        <Field label="Variações no anúncio">
          <div className="row wrap" style={{ gap: 8 }}>
            {product.variants.map((v) => {
              const on = variantIds.includes(v.id);
              return (
                <button
                  key={v.id}
                  type="button"
                  className={on ? 'chip on' : 'chip'}
                  aria-pressed={on}
                  onClick={() =>
                    setVariantIds(on ? variantIds.filter((x) => x !== v.id) : [...variantIds, v.id])
                  }
                >
                  {v.name}
                </button>
              );
            })}
          </div>
        </Field>
      ) : null}
      <p className="xs muted">O preço anunciado vem da aba Preços.</p>
    </Modal>
  );
}

/** "Anúncios" tab (board Produto-Visao, Anúncios). */
export function ListingsTab({ product }: { product: Product }) {
  const listings = useQuery({
    queryKey: listingsKey(product.id),
    queryFn: () => api.get(`/products/${product.id}/listings`, ListingList),
  });
  const toast = useToast();
  const [editing, setEditing] = useState<Listing | 'new' | null>(null);
  const rows = listings.data?.data ?? [];
  const groups = [...new Map(rows.map((l) => [l.channel.id, l.channel])).values()];
  const cols = '2fr 1fr 1.6fr 1.4fr 90px 170px';
  return (
    <section className="card flat" style={{ overflowX: 'auto' }}>
      <div className="row between wrap" style={{ padding: '16px 18px 6px', gap: 10 }}>
        <div>
          <h2>Anúncios por canal</h2>
          <p className="xs muted">
            O preço vem da aba Preços; aqui ficam título, ID e link de cada anúncio.
          </p>
        </div>
        <button type="button" className="btn sm p" onClick={() => setEditing('new')}>
          <Icon name="plus" size={16} />
          Anúncio
        </button>
      </div>
      {listings.data && rows.length === 0 ? (
        <div className="empty" style={{ padding: 24 }}>
          <p className="sm">
            Nenhum anúncio ainda. Cadastre o anúncio do Mercado Livre ou da Shopee para guardar ID e
            link.
          </p>
        </div>
      ) : null}
      <div className="gt" style={{ minWidth: 840 }}>
        {rows.length ? (
          <div className="gtr head" style={{ gridTemplateColumns: cols }}>
            <span>Título</span>
            <span>ID</span>
            <span>URL</span>
            <span>Preço anunciado</span>
            <span>Situação</span>
            <span />
          </div>
        ) : null}
        {groups.map((g) => {
          const own = rows.filter((l) => l.channel.id === g.id);
          return (
            <div key={g.id}>
              <div className="gtr gtgroup" style={{ gridTemplateColumns: '1fr auto' }}>
                <span className="row" style={{ gap: 8, fontWeight: 600 }}>
                  <span className="dot" style={{ background: g.color }} />
                  {g.name}
                </span>
                <span className="xs muted">
                  {own.length === 1 ? '1 anúncio' : `${own.length} anúncios`}
                </span>
              </div>
              {own.map((l) => (
                <div key={l.id} className="gtr" style={{ gridTemplateColumns: cols }}>
                  <span style={{ fontWeight: 500 }}>{l.title}</span>
                  <span className="tab sm">{l.externalId ?? '—'}</span>
                  <span className="xs muted" style={{ overflowWrap: 'anywhere' }}>
                    {l.url ?? '—'}
                  </span>
                  <span className="sm">{priceLine(l)}</span>
                  <span>
                    <span className={l.status === 'active' ? 'badge ok' : 'badge'}>
                      {l.status === 'active' ? 'Ativo' : 'Pausado'}
                    </span>
                  </span>
                  <span className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                    {l.url ? (
                      <button
                        type="button"
                        className="btn sm g"
                        onClick={() =>
                          void copyText(l.url ?? '').then((ok) =>
                            toast(ok ? 'Copiado. É só colar.' : 'Não deu para copiar.'),
                          )
                        }
                      >
                        Copiar link
                      </button>
                    ) : null}
                    <button type="button" className="btn sm" onClick={() => setEditing(l)}>
                      Editar
                    </button>
                  </span>
                </div>
              ))}
            </div>
          );
        })}
      </div>
      {editing ? (
        <ListingDialog
          product={product}
          listing={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </section>
  );
}
